/**
 * A turn's blocks in a live session (D372), drawn as the workbench draws them under a message:
 *
 *  - `ui`: a generative UI block (D354), drawn by the renderer below in the block's glass frame with its header line.
 *    Since workbench D414 the person who controls the session answers it here: its fields take input and a button whose
 *    action is "send" calls `ui.press` with the block's own action object and the values (`via: "web"`); a refusal is
 *    the workbench's sentence. A workbench before it answers -32601: the block is read only again and its foot says
 *    where to answer. A block with a field this console cannot draw stays read only.
 *  - `a2ui`: an agent's small form (Column, Row, Text, Markdown, Button, TextField, Chip, Divider), also embedded in its
 *    words as a fenced ```a2ui block. A press sends the person's answer as their next message (`sessions.send`), as the
 *    desktop does; a form that asks several things stages each pick and sends them together.
 *  - `ui-resource`: an MCP app. HTML runs only in a frame of its own (`/app-frame.html`), sandboxed without
 *    allow-same-origin so its origin is opaque and it reaches nothing of the console's; its messages are read only from
 *    that frame. A message the app sends goes to the session as the person's (when they control it); a tool it asks for
 *    is relayed into the conversation, never run here. A page named by a uri-list opens in a new tab.
 *
 * Copied from the workbench's webview-ui/src/components/facilitator/MessageBlocks.tsx (A2uiRenderer, MessageBlockList),
 * McpAppFrame.tsx, UiBlock.tsx and components/ui/uiRenderer/UiSpecView.tsx (0.90.0), with the desktop's host messages
 * swapped for the relay's control calls and the renderer kept to its read-only part; the rules are `@aioe/shared`'s
 * (ui/a2uiModel.ts, ui/uiResourceModel.ts, ui/uiSpecModel.ts).
 */

import type { LiveMessageBlock, UiAction, UiNode, UiSpecV1, UiValue, UiValues } from '@aioe/shared';
import {
  cleanValues,
  fieldsOf,
  isRemotePress,
  PRESS_WORDS,
  pressableBlock,
  pressProblem,
  A2UI_BROKEN,
  a2uiRoot,
  composeA2uiAnswer,
  fieldDefaults,
  findOptionGroups,
  groupOfButton,
  hasSubmitButton,
  isSubmitButton,
  progressValue,
  resolveA2uiMessage,
  resolveRows,
  resolveSteps,
  uiResourceToolLine,
  uiResourceView,
  validateUiSpec,
  type A2uiNode,
} from '@aioe/shared';
import { ArrowUpRightFromSquare, Check, Sparkles } from '@gravity-ui/icons';
import { Chip, Separator } from '@heroui/react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Markdown } from '../Markdown.tsx';
import { ArtStat, Button, ConfirmDialog } from '../ui.tsx';
import { StepTrack } from './RichMarkdown.tsx';
import './liveBlocks.css';

/** What a press on a generative UI block came to: it went, the workbench refused it (its words), or it predates `ui.press`. */
export type PressOutcome = { ok: true } | { ok: false; problem: string } | { ok: false; unsupported: true };

/** What a block may do from here: send the person's answer as their next message, or nothing (watching, unreachable). */
export interface BlockActions {
  /** Resolves with the platform's words when it did not go, undefined when it did. Absent: the blocks are inert. */
  send?: (text: string) => Promise<string | undefined>;
  /**
   * D414: a press on a generative UI block (`ui.press`), in a conversation the person controls. Absent: UI blocks are
   * read only (watching, unreachable, an agent session of its own, or a workbench before D414).
   */
  press?: (messageId: string, blockId: string, action: UiAction, values: UiValues) => Promise<PressOutcome>;
  /** The conversation the blocks are in (a press names it). */
  conversationId?: string;
  /** Whose workbench a block is answered on, for the line under one that cannot be answered here. */
  whose: string;
}

// ── A2UI ────────────────────────────────────────────────────────────────

interface A2uiForm {
  values: Record<string, string>;
  setValue: (id: string, value: string) => void;
  choices: Record<string, { label: string; message: string }>;
  choose: (group: string, label: string, message: string) => void;
  groups: string[];
  staged: boolean;
  submit: (extra?: string) => void;
  act: (message: string) => void;
  live: boolean;
}

function A2uiNodeView({ node, path, form }: { node: A2uiNode; path: string; form: A2uiForm }): ReactNode {
  const children = (node.children ?? []).map((c, i) => <A2uiNodeView key={i} node={c} path={`${path}.${i}`} form={form} />);
  switch (node.type) {
    case 'Column':
      return <div className="flex min-w-0 flex-col gap-2">{children}</div>;
    case 'Row':
      return <div className="flex min-w-0 flex-wrap items-center gap-2">{children}</div>;
    case 'Text':
      return <p className="m-0 text-xs text-foreground">{node.text ?? ''}</p>;
    case 'Markdown':
      return <Markdown breaks text={node.text ?? ''} className="min-w-0 text-xs" />;
    case 'Chip':
      return (
        <Chip size="sm" variant="soft">
          {node.text ?? node.label ?? ''}
        </Chip>
      );
    case 'Divider':
      return <Separator />;
    case 'TextField':
      return (
        <input
          className="ui-input min-w-0 flex-1 text-xs"
          placeholder={node.placeholder ?? node.label ?? 'Type your answer and press Enter'}
          aria-label={node.label ?? node.placeholder ?? 'Your answer'}
          value={node.id ? (form.values[node.id] ?? '') : undefined}
          readOnly={!form.live}
          onChange={(e) => node.id && form.setValue(node.id, e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.shiftKey || !form.live) return;
            const text = e.currentTarget.value.trim();
            if (!text) return;
            e.preventDefault();
            form.submit(text);
            if (node.id) form.setValue(node.id, '');
          }}
        />
      );
    case 'Button': {
      const message = node.action?.message ?? node.label ?? node.text ?? '';
      const group = groupOfButton(path, form.groups);
      const isOption = form.staged && !!group && !isSubmitButton(node);
      const picked = isOption && !!group && form.choices[group]?.label === (node.label ?? node.text);
      return (
        <Button
          size="sm"
          variant={picked || node.variant === 'primary' ? 'primary' : 'tertiary'}
          isDisabled={!form.live}
          aria-pressed={isOption ? picked : undefined}
          onPress={() => {
            if (!message) return;
            if (isOption && group) form.choose(group, node.label ?? node.text ?? message, message);
            else if (form.staged) form.submit(resolveA2uiMessage(message, form.values));
            else form.act(resolveA2uiMessage(message, form.values));
          }}
        >
          {picked ? <Check className="size-3" /> : null}
          {node.label ?? node.text ?? 'Action'}
        </Button>
      );
    }
    default:
      if (children.length) return <div className="flex min-w-0 flex-col gap-2">{children}</div>;
      return node.text ? <p className="m-0 text-xs text-muted">{node.text}</p> : null;
  }
}

/** An agent's form: its answer goes to the session as the person's next message. */
export function A2uiCard({ spec, actions }: { spec: unknown; actions: BlockActions }) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [choices, setChoices] = useState<Record<string, { label: string; message: string }>>({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [problem, setProblem] = useState<string>();
  const root = a2uiRoot(spec);
  const groups = useMemo(() => (root ? findOptionGroups(root) : []), [root]);
  const submitOwn = useMemo(() => (root ? hasSubmitButton(root) : false), [root]);
  if (!root) return null;
  const live = Boolean(actions.send) && !busy && !sent;
  const act = (message: string) => {
    if (!actions.send || !message.trim()) return;
    setBusy(true);
    setProblem(undefined);
    void actions.send(message).then((p) => {
      setBusy(false);
      if (p) setProblem(p);
      else setSent(true);
    });
  };
  const staged = groups.length > 1;
  const submit = (extra?: string) => act(composeA2uiAnswer(groups, choices, values, extra));
  const answered = groups.filter((g) => choices[g]).length;
  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-separator bg-surface/60 p-3" data-a2ui>
      <A2uiNodeView
        node={root}
        path="r"
        form={{
          values,
          setValue: (id, v) => setValues((x) => ({ ...x, [id]: v })),
          choices,
          choose: (g, label, message) => setChoices((x) => ({ ...x, [g]: { label, message } })),
          groups,
          staged,
          submit,
          act,
          live,
        }}
      />
      {staged && live && !submitOwn ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-separator pt-2">
          <Button size="sm" variant="primary" isDisabled={answered === 0} onPress={() => submit()}>
            {answered > 0 ? `Send ${answered} of ${groups.length} answers` : 'Send answers'}
          </Button>
          <span className="text-xs text-muted">Pick an option for each question; nothing goes until you press Send.</span>
        </div>
      ) : null}
      {sent ? <p className="m-0 text-xs text-muted">Sent. The agent has your answer.</p> : !actions.send ? <p className="m-0 text-xs text-muted">Only the person it runs for answers this, here or on {actions.whose}.</p> : null}
      {problem ? (
        <p className="m-0 text-xs text-danger" role="alert">
          {problem}
        </p>
      ) : null}
    </div>
  );
}

/** Shown in place of a form whose JSON could not be recovered. */
export function A2uiBroken() {
  return <p className="m-0 rounded-lg border border-separator bg-surface/60 px-3 py-2 text-xs italic text-muted">{A2UI_BROKEN}</p>;
}

// ── An MCP app ──────────────────────────────────────────────────────────

const MIN_HEIGHT = 80;
const MAX_HEIGHT = 900;
const REFUSED = -32000;
const NOT_FOUND = -32601;

/** The app's HTML in a sandboxed frame of its own; what it says is read only from that frame. */
function AppFrame({ html, uri, actions }: { html: string; uri: string; actions: BlockActions }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(240);
  const latest = useRef(actions);
  useEffect(() => {
    latest.current = actions;
  });
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const post = (m: unknown) => el.contentWindow?.postMessage(m, '*');
    const resize = (v: unknown) => {
      if (typeof v === 'number' && Number.isFinite(v)) setHeight(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.round(v))));
    };
    const theme = () => (document.documentElement.classList.contains('dark') ? 'dark' : 'light');
    const hostContext = () => ({ theme: theme(), locale: navigator.language, viewport: { width: el.clientWidth, height: el.clientHeight }, userAgent: 'aioe-console' });
    const say = (text: string) => {
      const send = latest.current.send;
      if (send && text.trim()) void send(text);
      return Boolean(send);
    };
    const open = (url: unknown) => {
      if (typeof url === 'string' && /^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener,noreferrer');
    };
    const onMessage = (e: MessageEvent) => {
      if (e.source !== el.contentWindow) return;
      const d = e.data as Record<string, unknown> | null;
      if (!d || typeof d !== 'object') return;
      if (d.type === 'aioe-app-frame-ready') {
        post({ type: 'aioe-app-frame-html', html });
        return;
      }
      // MCP Apps: JSON-RPC 2.0.
      if (d.jsonrpc === '2.0' && typeof d.method === 'string') {
        const id = d.id;
        const isRequest = id !== undefined && id !== null;
        const params = (d.params ?? {}) as Record<string, unknown>;
        const result = (r: unknown) => isRequest && post({ jsonrpc: '2.0', id, result: r });
        const error = (code: number, message: string) => isRequest && post({ jsonrpc: '2.0', id, error: { code, message } });
        switch (d.method) {
          case 'ui/initialize':
            result({ protocolVersion: '2025-11-25', hostContext: hostContext(), capabilities: { tools: { relayOnly: true }, ui: { openLink: true, message: true } } });
            return;
          case 'ui/notifications/size-changed':
            resize(params.height);
            result({});
            return;
          case 'ui/message': {
            const text = params.content ?? params.text ?? params.message;
            const live = typeof text === 'string' ? say(text) : Boolean(latest.current.send);
            if (live) result({});
            else error(REFUSED, 'This app is shown read-only here.');
            return;
          }
          case 'ui/open-link':
            open(params.url);
            result({});
            return;
          case 'tools/call':
            if (typeof params.name === 'string') say(uiResourceToolLine(params.name, params.arguments));
            error(REFUSED, 'Tool calls from embedded apps are relayed to the agent for approval, not run here.');
            return;
          case 'notifications/message':
            result({});
            return;
          default:
            error(NOT_FOUND, `Unsupported method: ${d.method}`);
        }
        return;
      }
      // Legacy mcp-ui: bare typed envelopes.
      if (typeof d.type !== 'string') return;
      const messageId = typeof d.messageId === 'string' ? d.messageId : undefined;
      const payload = (d.payload ?? {}) as Record<string, unknown>;
      const ack = () => messageId && post({ type: 'ui-message-received', messageId });
      const respond = (response: unknown, err?: unknown) => messageId && post({ type: 'ui-message-response', messageId, payload: { response, error: err } });
      switch (d.type) {
        case 'ui-lifecycle-iframe-ready':
        case 'ui-request-render-data':
          post({ type: 'ui-lifecycle-iframe-render-data', messageId, payload: { renderData: { hostContext: hostContext() } } });
          return;
        case 'ui-size-change':
          resize(payload.height);
          return;
        case 'prompt':
          ack();
          respond({ delivered: typeof payload.prompt === 'string' ? say(payload.prompt) : false });
          return;
        case 'intent':
          ack();
          respond({ delivered: typeof payload.intent === 'string' ? say(uiResourceToolLine(payload.intent, payload.params)) : false });
          return;
        case 'tool':
          ack();
          if (typeof payload.toolName === 'string') say(uiResourceToolLine(payload.toolName, payload.params));
          respond(undefined, { code: REFUSED, message: 'Tool calls are relayed to the agent for approval, not run here.' });
          return;
        case 'link':
          ack();
          open(payload.url);
          respond({ opened: true });
          return;
        case 'notify':
          ack();
          respond({ received: true });
          return;
        case 'ui-request-data':
          ack();
          respond(undefined, { code: REFUSED, message: 'Unsupported request.' });
          return;
        default:
          return;
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      post({ jsonrpc: '2.0', method: 'ui/resource-teardown', params: {} });
      window.removeEventListener('message', onMessage);
    };
  }, [html]);
  return (
    <iframe
      ref={frame}
      title={`App: ${uri}`}
      src="/app-frame.html"
      className="w-full rounded-xl border border-separator bg-surface"
      style={{ height }}
      // No allow-same-origin: the app's origin is opaque, so it reaches nothing of the console's (storage, cookies, page).
      sandbox="allow-scripts allow-forms"
      referrerPolicy="no-referrer"
      onLoad={() => frame.current?.contentWindow?.postMessage({ type: 'aioe-app-frame-html', html }, '*')}
      data-app-frame
    />
  );
}

/** An MCP app resource: its frame, the page it names, or what it is when it cannot be shown. */
export function UiResourceCard({ block, actions }: { block: Extract<LiveMessageBlock, { kind: 'ui-resource' }>; actions: BlockActions }) {
  const view = useMemo(() => uiResourceView(block), [block]);
  if (!view) return null;
  if (view.kind === 'html') return <AppFrame html={view.html} uri={view.uri} actions={actions} />;
  if (view.kind === 'link')
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-separator bg-surface/60 px-3 py-2 text-xs">
        <span className="min-w-0 flex-1 text-muted">The agent shared an app page.</span>
        <a href={view.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">
          Open it
          <ArrowUpRightFromSquare className="size-3" />
        </a>
      </div>
    );
  return (
    <div className="rounded-xl border border-separator bg-surface/60 p-3 text-xs">
      <p className="m-0 mb-1 text-muted">
        An app resource of a kind the console cannot show here (<span className="font-mono">{view.mimeType}</span>).
      </p>
      {view.body ? <pre className="m-0 max-h-48 overflow-auto whitespace-pre-wrap break-words text-xs text-muted">{view.body}</pre> : null}
    </div>
  );
}

// ── A generative UI block, read only ────────────────────────────────────

const GAPS = { s: 'ui-gap-s', m: 'ui-gap-m', l: 'ui-gap-l' } as const;
type FieldNode = Extract<UiNode, { type: 'field-text' | 'field-select' | 'field-number' | 'field-toggle' }>;
const isField = (n: UiNode): n is FieldNode => n.type === 'field-text' || n.type === 'field-select' || n.type === 'field-number' || n.type === 'field-toggle';

/** The fields this console draws with input; a block with any other field stays read only. */
const DRAWN_FIELDS = new Set(['field-text', 'field-select', 'field-number', 'field-toggle', 'choice-cards']);

/** A block being answered here (D414): the fields take input and a "send" press goes as `ui.press`. */
interface SpecForm {
  setValue: (name: string, value: UiValue | undefined) => void;
  press: (action: UiAction, label: string) => void;
  /** The label of the button whose press is going. */
  pressing?: string;
}

/**
 * One node, as the workbench's renderer draws it. Without `form` it takes no input (a press is the workbench's to
 * take); with it the fields take input and a button whose action is "send" presses.
 */
function SpecNode({ node, values, form }: { node: UiNode; values: UiValues; form?: SpecForm }): ReactNode {
  switch (node.type) {
    case 'stack': {
      const parts: ReactNode[] = [];
      for (let i = 0; i < node.children.length; ) {
        const c = node.children[i]!;
        const kind = c.type === 'stat' ? 'stat' : isField(c) ? 'field' : undefined;
        if (!kind) {
          parts.push(<SpecNode key={i} node={c} values={values} form={form} />);
          i++;
          continue;
        }
        const run: Array<{ n: UiNode; at: number }> = [];
        while (i < node.children.length && (kind === 'stat' ? node.children[i]!.type === 'stat' : isField(node.children[i]!))) {
          run.push({ n: node.children[i]!, at: i });
          i++;
        }
        parts.push(
          <div key={`run${run[0]!.at}`} className={kind === 'stat' ? 'ui-stats' : 'ui-fields'} style={{ ['--ui-cols' as string]: Math.min(run.length, kind === 'stat' ? 4 : 3) }}>
            {run.map(({ n, at }) => (
              <SpecNode key={at} node={n} values={values} form={form} />
            ))}
          </div>,
        );
      }
      return <div className={`ui-stack ${node.direction === 'row' ? 'ui-row' : ''} ${GAPS[node.gap ?? 'm']}`}>{parts}</div>;
    }
    case 'text':
      return (
        <p className="ui-text" data-emphasis={node.emphasis ?? 'normal'}>
          {node.text}
        </p>
      );
    case 'heading':
      return node.level === 1 ? <h2 className="ui-heading">{node.text}</h2> : <h3 className="ui-heading" data-level={node.level ?? 2}>{node.text}</h3>;
    case 'divider':
      return <Separator />;
    case 'card':
      return (
        <section className="card-art ui-card" data-tone={node.tone ?? 'slate'}>
          {node.title ? <h4 className="ui-card__title">{node.title}</h4> : null}
          <div className="ui-stack ui-gap-s">
            {node.children.map((c, i) => (
              <SpecNode key={i} node={c} values={values} form={form} />
            ))}
          </div>
        </section>
      );
    case 'choice-cards':
      return (
        <div className="ui-choice">
          {node.label ? <span className="ui-label">{node.label}</span> : null}
          <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0" role={form ? 'radiogroup' : undefined} aria-label={form ? (node.label ?? node.name) : undefined}>
            {node.options.map((o) => (
              <li key={o.value}>
                {form ? (
                  <Button size="sm" variant={values[node.name] === o.value ? 'primary' : 'tertiary'} aria-pressed={values[node.name] === o.value} onPress={() => form.setValue(node.name, o.value)}>
                    {values[node.name] === o.value ? <Check className="size-3" /> : null}
                    {o.label}
                  </Button>
                ) : (
                  <Chip size="sm" variant="soft" color={values[node.name] === o.value ? 'accent' : 'default'}>
                    {values[node.name] === o.value ? <Check className="size-3" /> : null}
                    {o.label}
                  </Chip>
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    case 'field-text':
    case 'field-select':
    case 'field-number':
    case 'field-toggle': {
      const v = values[node.name];
      if (form) return <FieldInput node={node} value={v} form={form} />;
      const shown = node.type === 'field-toggle' ? (v === true ? 'Yes' : 'No') : node.type === 'field-select' ? (node.options.find((o) => o.value === v)?.label ?? '') : v === undefined || v === null ? '' : String(v);
      return (
        <div className="ui-field">
          <span className="ui-label">{node.label}</span>
          <span className="ui-input flex items-center text-muted">{shown || '—'}</span>
        </div>
      );
    }
    case 'table': {
      const rows = resolveRows(node, undefined);
      return (
        <div className="ui-table-wrap">
          <table className="ui-table">
            {node.caption ? <caption>{node.caption}</caption> : null}
            <thead>
              <tr>
                {node.columns.map((c) => (
                  <th key={c.key} scope="col">
                    {c.label}
                  </th>
                ))}
                {node.rowActions?.length ? (
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  {node.columns.map((c) => (
                    <td key={c.key}>{r[c.key] === null || r[c.key] === undefined ? '' : String(r[c.key])}</td>
                  ))}
                  {node.rowActions?.length ? (
                    <td>
                      <div className="ui-actions">
                        {node.rowActions.map((a, j) => {
                          // As a button: only a "send" goes from here; any other action is the workbench's own.
                          const goes = Boolean(form) && isRemotePress(a.action);
                          return (
                            <Button
                              key={j}
                              size="sm"
                              variant="tertiary"
                              isDisabled={!goes || Boolean(form?.pressing)}
                              isPending={goes && form?.pressing === a.label}
                              aria-description={form && !goes ? PRESS_WORDS.otherAction : undefined}
                              onPress={goes ? () => form!.press(a.action, a.label) : undefined}
                            >
                              {a.label}
                            </Button>
                          );
                        })}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
              {!rows.length ? (
                <tr>
                  <td colSpan={node.columns.length + (node.rowActions?.length ? 1 : 0)} className="ui-table__empty">
                    Nothing to show
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      );
    }
    case 'stat':
      return <ArtStat tone={node.tone ?? 'slate'} label={node.label} value={String(node.value) || undefined} detail={node.hint ?? (node.trend ? { up: 'Rising', down: 'Falling', flat: 'Steady' }[node.trend] : undefined)} />;
    case 'progress': {
      const max = node.max ?? 100;
      const value = progressValue(node, undefined);
      const pct = Math.max(0, Math.min(100, (value / max) * 100));
      return (
        <div className="ui-progress" data-tone={node.tone ?? 'teal'}>
          {node.label ? <span className="ui-label">{`${node.label} · ${Math.round(pct)}%`}</span> : null}
          <div className="ui-progress__bar" role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={node.label ?? 'Progress'}>
            <span style={{ width: `${pct}%` }} />
          </div>
        </div>
      );
    }
    case 'steps': {
      const steps = resolveSteps(node, undefined);
      return steps.length ? <StepTrack steps={steps} label={node.label} tone={node.tone ?? 'teal'} /> : null;
    }
    case 'chip':
      return (
        <Chip size="sm" variant="soft">
          {node.text}
        </Chip>
      );
    case 'button': {
      // Only a "send" goes from here; any other action (open a view, run) is the workbench's own.
      const goes = Boolean(form) && isRemotePress(node.action);
      return (
        <Button
          size="sm"
          variant={node.variant === 'primary' ? 'primary' : 'tertiary'}
          isDisabled={!goes || Boolean(form?.pressing)}
          isPending={goes && form?.pressing === node.label}
          aria-description={form && !goes ? PRESS_WORDS.otherAction : undefined}
          onPress={goes ? () => form!.press(node.action, node.label) : undefined}
        >
          {node.label}
        </Button>
      );
    }
    case 'actions':
      return (
        <div className="ui-actions">
          {node.buttons.map((b, i) => (
            <SpecNode key={i} node={b} values={values} form={form} />
          ))}
        </div>
      );
    case 'link':
      return <span className="text-xs text-muted">{node.label}</span>;
    default:
      return <p className="ui-text" data-emphasis="muted">This part shows on the workbench.</p>;
  }
}

/** A field that takes input, for a block being answered here. */
function FieldInput({ node, value, form }: { node: Extract<UiNode, { type: 'field-text' | 'field-select' | 'field-number' | 'field-toggle' }>; value: UiValue | undefined; form: SpecForm }) {
  const id = `ui-field-${node.name}`;
  if (node.type === 'field-toggle') {
    return (
      <label className="flex min-w-0 items-center gap-2" htmlFor={id}>
        <input id={id} type="checkbox" className="size-4 accent-[var(--accent)]" checked={value === true} onChange={(e) => form.setValue(node.name, e.target.checked)} />
        <span className="ui-label">{node.label}</span>
      </label>
    );
  }
  return (
    <div className="ui-field">
      <label className="ui-label" htmlFor={id}>
        {node.label}
        {'required' in node && node.required ? <span className="text-muted"> (required)</span> : null}
      </label>
      {node.type === 'field-select' ? (
        <select id={id} className="ui-input" value={typeof value === 'string' ? value : ''} onChange={(e) => form.setValue(node.name, e.target.value || undefined)}>
          <option value="">Choose…</option>
          {node.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      ) : node.type === 'field-number' ? (
        <input id={id} type="number" className="ui-input" min={node.min} max={node.max} step={node.step} value={typeof value === 'number' ? value : ''} onChange={(e) => form.setValue(node.name, e.target.value === '' ? undefined : Number(e.target.value))} />
      ) : node.multiline ? (
        <textarea id={id} className="ui-input min-h-16" placeholder={node.placeholder} value={typeof value === 'string' ? value : ''} onChange={(e) => form.setValue(node.name, e.target.value)} />
      ) : (
        <input id={id} className="ui-input" placeholder={node.placeholder} value={typeof value === 'string' ? value : ''} onChange={(e) => form.setValue(node.name, e.target.value)} />
      )}
    </div>
  );
}

/**
 * A generative UI block: what the agent put in front of the person, with what was answered or where to answer it. The
 * person who controls the conversation answers it here (D414 `ui.press`), once; the stream's answered block follows.
 */
export function UiBlockCard({ block, actions, messageId }: { block: Extract<LiveMessageBlock, { kind: 'ui' }>; actions: BlockActions; messageId?: string }) {
  const spec = useMemo(() => {
    const checked = validateUiSpec(block.spec, { allowRun: true });
    return checked.ok ? (checked.spec as UiSpecV1) : undefined;
  }, [block.spec]);
  const shown = useMemo<UiValues>(() => (spec ? { ...fieldDefaults(spec), ...((block.answered?.values as UiValues | undefined) ?? {}) } : {}), [spec, block.answered]);
  const [typed, setTyped] = useState<UiValues>();
  const [pressing, setPressing] = useState<string>();
  const [outcome, setOutcome] = useState<PressOutcome>();
  const [confirming, setConfirming] = useState<{ action: Extract<UiAction, { do: 'send' }>; label: string }>();
  const press = actions.press;
  // Answerable here: a conversation the person controls, a block not yet answered with a "send" in it, every field drawable.
  const answerable = Boolean(press && messageId) && pressableBlock(block, spec, actions.conversationId) && !!spec && fieldsOf(spec).every((f) => DRAWN_FIELDS.has(f.type)) && !(outcome && (outcome.ok || 'unsupported' in outcome));
  const values = typed ?? shown;
  const go = async (action: UiAction, label: string) => {
    if (!press || !messageId || !spec) return;
    const problem = pressProblem(spec, values);
    if (problem) return setOutcome({ ok: false, problem });
    setPressing(label);
    setOutcome(undefined);
    const r = await press(messageId, block.id, action, cleanValues(spec, values));
    setPressing(undefined);
    setOutcome(r);
  };
  const form: SpecForm | undefined = answerable
    ? {
        setValue: (name, value) =>
          setTyped((v) => {
            const next = { ...(v ?? shown) };
            if (value === undefined) delete next[name];
            else next[name] = value;
            return next;
          }),
        press: (action, label) => {
          if (!isRemotePress(action)) return;
          if (action.confirm) setConfirming({ action, label });
          else void go(action, label);
        },
        ...(pressing ? { pressing } : {}),
      }
    : undefined;
  if (block.proposal) return <p className="m-0 rounded-xl border border-separator bg-surface/60 px-3 py-2 text-xs text-muted">The agent proposed an app. Save or dismiss it on {actions.whose}.</p>;
  if (!spec) return <p className="m-0 rounded-xl border border-separator bg-surface/60 px-3 py-2 text-xs text-muted">The agent showed a block this console cannot draw. It shows on {actions.whose}.</p>;
  return (
    <section className="ui-block" aria-label={spec.title ?? spec.alt} data-ui-block={block.id} data-answered={block.answered ? '' : undefined}>
      <header className="ui-block__head">
        <span className="ui-block__icon" aria-hidden="true">
          <Sparkles className="size-4" />
        </span>
        <span className="ui-block__title">{spec.title ?? spec.alt}</span>
      </header>
      <div className="ui-block__body" data-inert={form ? undefined : ''} data-answerable={form ? '' : undefined}>
        <SpecNode node={spec.root} values={values} form={form} />
      </div>
      <div className="ui-block__foot" role={outcome && !outcome.ok && 'problem' in outcome ? 'alert' : undefined}>
        {block.answered ? (
          block.answered.line
        ) : outcome?.ok ? (
          PRESS_WORDS.sent
        ) : outcome && 'unsupported' in outcome ? (
          PRESS_WORDS.older(actions.whose)
        ) : outcome && 'problem' in outcome ? (
          <span className="text-danger">{outcome.problem}</span>
        ) : pressing ? (
          PRESS_WORDS.sending
        ) : form ? (
          PRESS_WORDS.open
        ) : (
          PRESS_WORDS.elsewhere(actions.whose)
        )}
      </div>
      {confirming ? (
        <ConfirmDialog
          isOpen
          title={confirming.label}
          confirmLabel={confirming.label}
          onCancel={() => setConfirming(undefined)}
          onConfirm={async () => {
            const c = confirming;
            setConfirming(undefined);
            await go(c.action, c.label);
          }}
        >
          <p className="m-0">{confirming.action.confirm}</p>
        </ConfirmDialog>
      ) : null}
    </section>
  );
}

/** A turn's blocks, in order. `messageId`: the turn's, which a press on a UI block names. */
export function MessageBlocks({ blocks, actions, messageId }: { blocks: readonly LiveMessageBlock[]; actions: BlockActions; messageId?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-2" data-message-blocks>
      {blocks.map((b, i) =>
        b.kind === 'ui' ? <UiBlockCard key={b.id || i} block={b} actions={actions} messageId={messageId} /> : b.kind === 'ui-resource' ? <UiResourceCard key={`${b.uri}-${i}`} block={b} actions={actions} /> : <A2uiCard key={i} spec={b.spec} actions={actions} />,
      )}
    </div>
  );
}
